import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useOnlineStatus } from './useOnlineStatus'

export function useMobileNotifications(userProfileId) {
  const isOnline = useOnlineStatus()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const channelRef = useRef(null)

  const fetchNotifications = useCallback(async () => {
    if (!userProfileId || !isOnline) return
    try {
      const { data, error } = await supabase.rpc('app_get_notifications_v2', {
        p_user_id: userProfileId,
        p_limit: 30,
        p_offset: 0,
      })
      if (error) throw error
      setNotifications(data?.notifications || [])
      setUnreadCount(data?.unread_count || 0)
    } catch (err) {
      console.error('[Notifications] Error:', err)
    } finally {
      setLoading(false)
    }
  }, [userProfileId, isOnline])

  const fetchUnreadCount = useCallback(async () => {
    if (!userProfileId || !isOnline) return
    try {
      const { data, error } = await supabase.rpc('app_get_unread_notification_count_v2', {
        p_user_id: userProfileId,
      })
      if (error) throw error
      setUnreadCount(data || 0)
    } catch (err) {
      console.error('[Notifications] Count error:', err)
    }
  }, [userProfileId, isOnline])

  const markAsRead = useCallback(async (notificationId) => {
    if (!userProfileId) return
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    )
    setUnreadCount(prev => Math.max(0, prev - 1))
    try {
      await supabase.rpc('app_mark_notification_read_v2', {
        p_user_id: userProfileId,
        p_notification_id: notificationId,
      })
    } catch (err) {
      console.error('[Notifications] Mark read error:', err)
    }
  }, [userProfileId])

  const markAllAsRead = useCallback(async () => {
    if (!userProfileId) return
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    setUnreadCount(0)
    try {
      await supabase.rpc('app_mark_all_notifications_read_v2', {
        p_user_id: userProfileId,
      })
    } catch (err) {
      console.error('[Notifications] Mark all read error:', err)
    }
  }, [userProfileId])

  // Initial fetch
  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  // Real-time subscription
  useEffect(() => {
    if (!userProfileId || !isOnline) return

    const channel = supabase
      .channel(`mobile-notifications:${userProfileId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userProfileId}`,
        },
        () => {
          // Debounce refetch slightly to batch rapid updates
          setTimeout(() => fetchNotifications(), 500)
        }
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
      }
    }
  }, [userProfileId, isOnline, fetchNotifications])

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  }
}
